import { useCallback, useEffect, useRef, useState } from "react";
import SignaturePadLibrary, { type PointGroup } from "signature_pad";

const SIGNATURE_WIDTH = 600;
const SIGNATURE_HEIGHT = 200;
const SIGNATURE_FILE_NAME = "signature.png";

interface CanvasSize {
  width: number;
  height: number;
}

interface UseSignatureCanvasOptions {
  readonly onChange: (file: File | null) => void;
  readonly disabled: boolean;
  readonly onCaptureBusyChange?: (busy: boolean) => void;
}

const resizeSignatureData = (
  data: PointGroup[],
  widthScale: number,
  heightScale: number,
): PointGroup[] =>
  data.map((group) => ({
    ...group,
    points: group.points.map((point) => ({
      ...point,
      x: point.x * widthScale,
      y: point.y * heightScale,
    })),
  }));

export const useSignatureCanvas = ({
  onChange,
  disabled,
  onCaptureBusyChange,
}: UseSignatureCanvasOptions) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePadLibrary | null>(null);
  const canvasSizeRef = useRef<CanvasSize>({ width: 0, height: 0 });
  const onChangeRef = useRef(onChange);
  const onCaptureBusyChangeRef = useRef(onCaptureBusyChange);
  const exportRequestRef = useRef(0);
  const isDrawingRef = useRef(false);
  const disabledRef = useRef(disabled);
  const publishSignatureRef = useRef<(() => void) | null>(null);

  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    onChangeRef.current = onChange;
    onCaptureBusyChangeRef.current = onCaptureBusyChange;
    disabledRef.current = disabled;
  }, [onChange, onCaptureBusyChange, disabled]);

  const stopDrawing = useCallback(() => {
    const signaturePad = signaturePadRef.current;

    if (!signaturePad || !isDrawingRef.current) return;

    isDrawingRef.current = false;
    // clear()/fromData() alone leave an active pointer stroke stuck in v5.1.3.
    signaturePad.off();
    if (!disabledRef.current) signaturePad.on();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const signaturePad = new SignaturePadLibrary(canvas, {
      penColor: "#000000",
      backgroundColor: "rgba(0, 0, 0, 0)",
      minWidth: 2,
      maxWidth: 4,
    });

    signaturePadRef.current = signaturePad;

    const exportSignature = () => {
      if (signaturePad.isEmpty()) return;

      const requestId = ++exportRequestRef.current;
      const exportCanvas = document.createElement("canvas");

      exportCanvas.width = SIGNATURE_WIDTH;
      exportCanvas.height = SIGNATURE_HEIGHT;

      const context = exportCanvas.getContext("2d");

      if (!context) {
        onChangeRef.current(null);
        onCaptureBusyChangeRef.current?.(false);
        return;
      }

      context.clearRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
      context.drawImage(canvas, 0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);

      exportCanvas.toBlob((blob) => {
        if (requestId !== exportRequestRef.current) return;

        if (!blob) {
          onChangeRef.current(null);
          onCaptureBusyChangeRef.current?.(false);
          return;
        }

        const file = new File([blob], SIGNATURE_FILE_NAME, {
          type: "image/png",
        });

        onChangeRef.current(file);
        onCaptureBusyChangeRef.current?.(false);
      }, "image/png");
    };

    const handleBeginStroke = () => {
      isDrawingRef.current = true;
      exportRequestRef.current += 1;
      onCaptureBusyChangeRef.current?.(true);
    };

    const publishSignature = () => {
      isDrawingRef.current = false;
      const signatureExists = !signaturePad.isEmpty();

      setHasSignature(signatureExists);

      if (signatureExists) {
        onCaptureBusyChangeRef.current?.(true);
        exportSignature();
      } else {
        onChangeRef.current(null);
        onCaptureBusyChangeRef.current?.(false);
      }
    };

    publishSignatureRef.current = publishSignature;

    const resizeCanvas = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;

      if (width === 0 || height === 0) return;

      const previousSize = canvasSizeRef.current;
      const signatureData = signaturePad.toData();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const canvasWidth = Math.round(width * ratio);
      const canvasHeight = Math.round(height * ratio);

      if (canvas.width === canvasWidth && canvas.height === canvasHeight) {
        return;
      }

      stopDrawing();
      exportRequestRef.current += 1;

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      canvas.getContext("2d")?.scale(ratio, ratio);
      canvasSizeRef.current = { width, height };

      if (
        signatureData.length === 0 ||
        previousSize.width === 0 ||
        previousSize.height === 0
      ) {
        signaturePad.clear();
        if (previousSize.width > 0 && previousSize.height > 0) {
          publishSignature();
        }
        return;
      }

      const resizedData = resizeSignatureData(
        signatureData,
        width / previousSize.width,
        height / previousSize.height,
      );

      signaturePad.fromData(resizedData);
      // Export the same redrawn signature that is visible after the resize.
      publishSignature();
    };

    const resizeObserver = new ResizeObserver(resizeCanvas);

    resizeObserver.observe(canvas);
    resizeCanvas();

    signaturePad.addEventListener("beginStroke", handleBeginStroke);
    signaturePad.addEventListener("endStroke", publishSignature);

    return () => {
      exportRequestRef.current += 1;
      isDrawingRef.current = false;
      resizeObserver.disconnect();
      signaturePad.removeEventListener("beginStroke", handleBeginStroke);
      signaturePad.removeEventListener("endStroke", publishSignature);
      signaturePad.off();
      signaturePadRef.current = null;
      publishSignatureRef.current = null;
      // The capture owner resets busy when replacing this canvas. Cleanup must
      // not release a newer canvas's capture through an obsolete callback.
    };
  }, [stopDrawing]);

  useEffect(() => {
    const signaturePad = signaturePadRef.current;

    if (!signaturePad) return;

    if (disabled) {
      const wasDrawing = isDrawingRef.current;
      if (wasDrawing) {
        stopDrawing();
        exportRequestRef.current += 1;
        // Keep the visible strokes and release busy only after their PNG is ready.
        publishSignatureRef.current?.();
      } else {
        signaturePad.off();
      }
    } else {
      signaturePad.on();
    }
  }, [disabled, stopDrawing]);

  const clearSignature = useCallback(() => {
    stopDrawing();
    exportRequestRef.current += 1;
    signaturePadRef.current?.clear();
    setHasSignature(false);
    onChangeRef.current(null);
    onCaptureBusyChangeRef.current?.(false);
  }, [stopDrawing]);

  return {
    canvasRef,
    hasSignature,
    clearSignature,
  };
};
